<template>
  <my-giving-layout>
    <div class="w-full min-h-screen bg-[#F6F4F0]">
      <BackButton class="w-full px-[20px] mb-[16px]" :customBack="back" />
      <InfoModal :is-open="showInfoModal" @close="showInfoModal = false" />
      <div class="flex flex-col gap-[24px] mx-[20px] md:mx-[24px] lg:mx-[28px]">
        <div class="flex flex-col">
          <div class="flex flex-row justify-between">
            <div
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem]"
            >
              Giving to
            </div>
            <div
              class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.25rem] lg:leading-[2.5rem] font-bold"
            >
              {{ selectedGiveType }}
            </div>
          </div>
          <div class="flex flex-row justify-between pt-[8px]">
            <div
              class="text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem]"
            >
              Giving method
            </div>
            <div
              class="text-[1rem] md:text-[1.75rem] lg:text-[1.875rem] leading-[1.5rem] md:leading-[2.25rem] lg:leading-[2.5rem] font-bold"
            >
              Local Bank Transfer
            </div>
          </div>
        </div>
        <p
          class="font-[700] text-[1.5rem] md:text-[2rem] lg:text-[2.25rem] leading-[2rem] md:leading-[2.5rem] lg:leading-[2.75rem] text-gray-800"
        >
          Select giving frequency
        </p>
        <div class="flex flex-row justify-between gap-[8px]">
          <label
            class="w-full rounded-[10px] border border-[1px] border-[#A5A4A3] py-[32px] flex items-center"
            :class="{
              'bg-[#E8DCC766] font-bold border-[2px]': selected === 'Monthly',
            }"
            @click="handleMonthly()"
          >
            <input
              class="w-[6.4px] h-[6.4px] ring-[4.0px] ring-white rounded-full bg-white checked:bg-[#42362B] shadow-[0_0_0_4.8px_#42362B] mr-[10px] ml-[12px]"
              type="radio"
              value="Monthly"
              v-model="selected"
            />
            Monthly
          </label>
          <label
            class="w-full rounded-[10px] border border-[1px] border-[#A5A4A3] py-[32px] flex items-center"
            :class="{
              'bg-[#E8DCC766] font-bold border-[2px]': selected === 'OneTime',
            }"
            @click="handleOneTime()"
          >
            <input
              class="w-[6.4px] h-[6.4px] ring-[4.0px] ring-white rounded-full bg-white checked:bg-[#42362B] shadow-[0_0_0_4.8px_#42362B] mr-[10px] ml-[12px]"
              type="radio"
              value="OneTime"
              v-model="selected"
            />
            One Time
          </label>
        </div>
        <div v-if="selected === 'OneTime'">
          Month
          <GivingDropdown
            :selected="selectedMonth"
            :options="months"
            @changeSelect="changeMonth"
            type="localTransfer"
          ></GivingDropdown>
        </div>
        <Vueform
          v-if="showPIDfields && selectedGiveType !== 'Offering'"
          class="w-full"
          :float-placeholders="false"
          :display-errors="false"
          :force-labels="false"
          :display-messages="false"
          v-model="formData"
        >
          <div class="col-span-12">
            <!-- DOB probably shouldn't change, but just added change in case there's a possibility or error 
             and changed due to that etc. -->
            <DateElement
              class="mb-4"
              name="dateOfBirth"
              label="Date of Birth"
              :placeholder="dobPlaceholderText"
              :default="this.originalPayload?.dateOfBirth || null"
              display-format="DD-MM-YYYY"
              :max="new Date()"
              :rules="['required', `regex:^\\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01])$`]"
              :messages="{
                required: 'Required',
                regex: `Enter a valid date`,
              }"
              @change="generateReferenceNumber()"
            />
            <!-- NRIC probably shouldn't change, but just added change in case there's a possibility or error 
             and changed due to that etc. -->
            <TextElement
              name="partialNric"
              label="Last 4 chars of NRIC/FIN/Passport no."
              :placeholder="partialNricPlaceholderText"
              :default="this.originalPayload?.partialNric || null"
              :rules="['required', `regex:/^[A-Za-z0-9]{4}$/`]"
              :messages="{
                required: 'Required',
                regex: `Must contain 4 characters`,
              }"
              @change="generateReferenceNumber()"
            />
            <div class="flex flex-row gap-1 items-center pt-[16px]">
              <img src="/assets/icons/my-giving/info.svg" class="w-[16px] h-[16px]" />
              <div
                @click="onInfo"
                class="block text-[0.875rem] leading-[1.25rem] font-inter font-medium text-[#0D5DF5] underline cursor-pointer"
              >
                What is this information used for?
              </div>
            </div>
          </div>
        </Vueform>
        <BottomButton
          v-if="!showInstructions && selectedGiveType !== 'Offering'"
          id="give-now"
          text="Give Now"
          :disabled="loading"
          @click="handleGive"
          class="w-full mb-[64px]"
        />
        <section v-if="showInstructions || selectedGiveType === 'Offering'">
          <div class="rounded-[16px] bg-[#E5E2DF] p-[16px] mb-[24px]">
            <h1
              class="font-[700] text-[1.5rem] md:text-[2rem] lg:text-[2.25rem] leading-[2rem] md:leading-[2.5rem] lg:leading-[2.75rem] text-[#21201E] pb-[12px]"
            >
              Instructions
            </h1>
            <p
              v-if="selected === 'Monthly'"
              class="font-[500] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] text-[#21201E] pb-[24px]"
            >
              Set up a "recurring transfer"or "standing instruction" with the following details:
            </p>
            <div
              class="flex flex-col gap-[8px] text-[0.875rem] md:text-[1.5rem] lg:text-[1.625rem] leading-[1.25rem] md:leading-[2rem] lg:leading-[2.25rem] text-[#21201E] pb-[24px]"
            >
              <p class="font-[700]">1. Add HOGC as “recipient” or “payee”:</p>
              <div class="flex flex-row">
                <p>Name:</p>
                <p class="font-[700]">&nbsp;Heart of God Church</p>
                <img
                  src="/assets/icons/my-giving/copyIcon.svg"
                  class="ml-[8px]"
                  @click="copyClipboard('Heart of God Church')"
                />
              </div>
              <div class="flex flex-row">
                <p>Account:</p>
                <p class="font-[700]">&nbsp;1000006671</p>
                <img
                  src="/assets/icons/my-giving/copyIcon.svg"
                  class="ml-[8px]"
                  @click="copyClipboard('1000006671')"
                />
              </div>
              <div class="flex flex-row">
                <p>Bank Name:</p>
                <p class="font-[700]">&nbsp;DBS Bank Ltd</p>
                <img
                  src="/assets/icons/my-giving/copyIcon.svg"
                  class="ml-[8px]"
                  @click="copyClipboard('DBS Bank Ltd')"
                />
              </div>
              <p class="italic text-[0.75rem] leading-[1rem]">
                *Skip to Step 2 if you have already added Heart of God Church as a payee.
              </p>
            </div>
            <div class="flex flex-col gap-[8px] text-[0.875rem] leading-[1.25rem] text-[#21201E]">
              <p class="font-[700]">2. Enter reference no.</p>
              <div class="flex flex-row">
                <p>Reference No.:</p>
                <p class="font-[700]">&nbsp;{{ referenceNumber }}</p>
                <img
                  src="/assets/icons/my-giving/copyIcon.svg"
                  class="ml-[8px]"
                  @click="copyClipboard(referenceNumber)"
                />
              </div>
              <p class="italic text-[0.75rem] leading-[1rem]">
                👤 If a Reference Number is not provided, the giving will be recorded as anonymous.
              </p>
            </div>
          </div>
          <BottomButton
            id="back"
            text="Back to MyGiving"
            :disabled="loading"
            @click="handleBack"
            class="w-full my-[32px]"
          />
        </section>
      </div>
    </div>
  </my-giving-layout>
</template>

<script>
import BackButton from '@/components/common/BackButton.vue';
import BottomButton from '@/components/common/BottomButton.vue';
import MasterLayout from '@/components/MasterLayout.vue';
import GivingDropdown from '@/components/MyGiving/BuildingFundPage/GivingDropdown.vue';
import InfoModal from '@/components/MyGiving/Common/InfoModal.vue';
import { churchTime } from '@/utils/date';
import { mapState, mapGetters } from 'vuex';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
export default {
  components: {
    MasterLayout,
    BackButton,
    BottomButton,
    GivingDropdown,
    InfoModal,
    MyGivingLayout,
  },
  data() {
    return {
      selected: 'Monthly',
      referenceNumber: 'OFFERING',
      months: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
      selectedMonth: 'January',
      dobPlaceholderText: 'Enter your Date of Birth',
      partialNricPlaceholderText: 'Enter last 4 Char of NRIC / FIN',
      showInfoModal: false,
      showInstructions: false,
      showPIDfields: true,
      formData: {
        dateOfBirth: null,
        partialNric: null,
      },
    };
  },
  computed: {
    ...mapState('myGiving', ['selectedGiveType']),
    ...mapGetters({
      originalPayload: 'profile/payload',
    }),
  },
  ionViewWillEnter() {
    // Always use English locale to prevent Chinese characters
    const currentMonth = new Date().toLocaleString('en-US', {
      month: 'long',
    });
    this.selectedMonth = currentMonth;
  },
  methods: {
    back() {
      this.showInstructions = false;
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.replace('/my-giving');
      }
    },
    changeMonth(month) {
      this.selectedMonth = month;
      this.generateReferenceNumber(); //added
    },
    copyClipboard(target) {
      navigator.clipboard.writeText(target);
    },
    onInfo() {
      // pull up modal
      this.showInfoModal = true;
    },
    handleMonthly() {
      this.selected = 'Monthly';
      this.showInstructions = false;
    },
    handleOneTime() {
      this.selected = 'OneTime';
      this.showInstructions = false;
    },
    handleGive() {
      this.showInstructions = true;
      this.generateReferenceNumber();
    },
    handleBack() {
      this.showInstructions = false;
      this.$router.replace('/my-giving');
    },
    generateReferenceNumber() {
      // personal ID
      if (this.formData.partialNric && this.formData.dateOfBirth) {
        this.personalID =
          this.formData.partialNric + churchTime(this.formData.dateOfBirth).format('DDMM');
      }
      if (this.selectedGiveType === 'Offering') {
        this.referenceNumber = 'OFFERING';
      } else {
        this.referenceNumber = (
          this.personalID + this.shortenGivingType(this.selectedGiveType)
        ).toUpperCase();
      }
    },
    shortenGivingType(type) {
      const monthText = this.selectedMonth.slice(0, 3).toUpperCase();
      switch (type) {
        case 'Tithe':
          return this.selected === 'Monthly' ? 'T' : monthText + 'T';
        case 'Building Fund':
          return this.selected === 'Monthly' ? 'B' : monthText + 'B';
        case 'Heart Community Services':
          return this.selected === 'Monthly' ? 'H' : monthText + 'H';
        case 'Missions':
          return this.selected === 'Monthly' ? 'M' : monthText + 'M';
        case 'GenerationS Fund':
          return 'GENS';
        default:
          return '';
      }
    },
  },
};
</script>
